'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, Pill, Snowflake, Brush, Calendar, CheckCircle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

gsap.registerPlugin(ScrollTrigger);

export default function AftercareSection() {
    const { t, isLoading } = useTranslation('root-canal');
    const ready = !isLoading;
    const { isRTL } = useRTL();
    const isMounted = useRef(false);
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const timelineRef = useRef<HTMLDivElement>(null);
    const guideRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!isMounted.current || !ready) return;

        const ctx = gsap.context(() => {
            // Title animation
            if (titleRef.current) {
                gsap.fromTo(titleRef.current,
                    { opacity: 0, y: 50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: titleRef.current,
                            start: "top 90%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Cards animation
            if (cardsRef.current?.children?.length) {
                gsap.fromTo(Array.from(cardsRef.current.children),
                    { opacity: 0, y: 30, scale: 0.95 },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.5,
                        stagger: 0.08,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: cardsRef.current,
                            start: "top 90%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Timeline animation
            if (timelineRef.current) {
                gsap.fromTo(timelineRef.current,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: timelineRef.current,
                            start: "top 90%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Guide download animation
            if (guideRef.current) {
                gsap.fromTo(guideRef.current,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: guideRef.current,
                            start: "top 90%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

        }, sectionRef);

        return () => ctx.revert();
    }, [ready]);

    const getAftercareItems = () => [
        {
            icon: Clock,
            title: t('aftercare.tips.avoidChewing.title'),
            description: t('aftercare.tips.avoidChewing.description')
        },
        {
            icon: Pill,
            title: t('aftercare.tips.takeMedications.title'),
            description: t('aftercare.tips.takeMedications.description')
        },
        {
            icon: Snowflake,
            title: t('aftercare.tips.manageSwelling.title'),
            description: t('aftercare.tips.manageSwelling.description')
        },
        {
            icon: Brush,
            title: t('aftercare.tips.maintainHygiene.title'),
            description: t('aftercare.tips.maintainHygiene.description')
        },
        {
            icon: Calendar,
            title: t('aftercare.tips.returnForCrown.title'),
            description: t('aftercare.tips.returnForCrown.description')
        },
        {
            icon: CheckCircle,
            title: t('aftercare.tips.resumeActivities.title'),
            description: t('aftercare.tips.resumeActivities.description')
        }
    ];

    const generateAftercarePDF = () => {
        // Create PDF content with aftercare tips and timeline
        const currentLang = isRTL ? (window.location.pathname.includes('/ar/') ? 'ar' : 'fa') : 'en';
        
        // Create a printable version of the content
        const printContent = `
            <!DOCTYPE html>
            <html dir="${isRTL ? 'rtl' : 'ltr'}" lang="${currentLang}">
            <head>
                <meta charset="UTF-8">
                <title>${t('aftercare.title.part1')} ${t('aftercare.title.part2')}</title>
                <style>
                    @page {
                        size: A4;
                        margin: 0.7in;
                    }
                    body {
                        font-family: 'Arial', sans-serif;
                        line-height: 1.4;
                        margin: 0;
                        padding: 0;
                        color: #333;
                        direction: ${isRTL ? 'rtl' : 'ltr'};
                        font-size: 14px;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 25px;
                        border-bottom: 3px solid #D2A138;
                        padding-bottom: 15px;
                    }
                    .title {
                        color: #222D52;
                        font-size: 24px;
                        font-weight: bold;
                        margin-bottom: 8px;
                    }
                    .subtitle {
                        color: #4A5568;
                        font-size: 14px;
                    }
                    .section {
                        margin-bottom: 25px;
                    }
                    .section-title {
                        color: #D2A138;
                        font-size: 18px;
                        font-weight: bold;
                        margin-bottom: 15px;
                        text-align: ${isRTL ? 'right' : 'left'};
                    }
                    .tips-grid {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 15px;
                        margin-bottom: 25px;
                    }
                    .tip-card {
                        border: 2px solid #E2E8F0;
                        border-radius: 8px;
                        padding: 15px;
                        background-color: #F8FAFC;
                        min-height: 100px;
                    }
                    .tip-title {
                        color: #222D52;
                        font-weight: bold;
                        margin-bottom: 8px;
                        text-align: ${isRTL ? 'right' : 'left'};
                        font-size: 14px;
                    }
                    .tip-description {
                        color: #4A5568;
                        text-align: ${isRTL ? 'right' : 'left'};
                        font-size: 12px;
                        line-height: 1.4;
                    }
                    .timeline {
                        display: grid;
                        grid-template-columns: repeat(3, 1fr);
                        gap: 15px;
                        margin-top: 15px;
                    }
                    .timeline-item {
                        text-align: center;
                        padding: 15px;
                        border: 2px solid #D2A138;
                        border-radius: 10px;
                        background-color: #FFF;
                        min-height: 120px;
                    }
                    .timeline-number {
                        background-color: #D2A138;
                        color: white;
                        width: 35px;
                        height: 35px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin: 0 auto 10px;
                        font-weight: bold;
                        font-size: 14px;
                    }
                    .timeline-title {
                        color: #222D52;
                        font-weight: bold;
                        margin-bottom: 8px;
                        font-size: 13px;
                    }
                    .timeline-description {
                        color: #4A5568;
                        text-align: ${isRTL ? 'right' : 'left'};
                        font-size: 11px;
                        line-height: 1.3;
                    }
                    .footer {
                        margin-top: 30px;
                        text-align: center;
                        padding-top: 15px;
                        border-top: 2px solid #E2E8F0;
                        color: #4A5568;
                        font-size: 12px;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1 class="title">${t('aftercare.title.part1')} ${t('aftercare.title.part2')}</h1>
                    <p class="subtitle">${t('aftercare.subtitle')}</p>
                </div>

                <div class="section">
                    <h2 class="section-title">${t('aftercare.subtitle')}</h2>
                    <div class="tips-grid">
                        ${getAftercareItems().map(item => `
                            <div class="tip-card">
                                <h3 class="tip-title">${item.title}</h3>
                                <p class="tip-description">${item.description}</p>
                            </div>
                        `).join('')}
                    </div>
                </div>

                <div class="section">
                    <h2 class="section-title">${t('aftercare.timeline.title')}</h2>
                    <div class="timeline">
                        <div class="timeline-item">
                            <div class="timeline-number">${isRTL ? '١' : '1'}</div>
                            <h3 class="timeline-title">${t('aftercare.timeline.phases.first24.title')}</h3>
                            <p class="timeline-description">${t('aftercare.timeline.phases.first24.description')}</p>
                        </div>
                        <div class="timeline-item">
                            <div class="timeline-number">${isRTL ? '٢' : '2'}</div>
                            <h3 class="timeline-title">${t('aftercare.timeline.phases.week1.title')}</h3>
                            <p class="timeline-description">${t('aftercare.timeline.phases.week1.description')}</p>
                        </div>
                        <div class="timeline-item">
                            <div class="timeline-number">✓</div>
                            <h3 class="timeline-title">${t('aftercare.timeline.phases.week2.title')}</h3>
                            <p class="timeline-description">${t('aftercare.timeline.phases.week2.description')}</p>
                        </div>
                    </div>
                </div>

                <div class="footer">
                    <p><strong>DentaWista</strong> - ${new Date().toLocaleDateString(currentLang === 'ar' ? 'ar-EG' : currentLang === 'fa' ? 'fa-IR' : 'en-US')}</p>
                    <p>${t('aftercare.guide.faqPrompt')} <strong>www.dentawista.com</strong></p>
                </div>
            </body>
            </html>
        `;

        // Create and open print window
        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 500);
        } else {
            alert(t('aftercare.guide.popupBlocked') || 'Please allow popups to download the aftercare guide.');
        }
    };

    return (
        <section id="aftercare" ref={sectionRef} className="py-16 lg:py-24 bg-white dark:bg-slate-800">
            <div className="container">
                <div ref={titleRef} className="text-center mb-16">
                    {!ready ? (
                        <div className="animate-pulse">
                            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-4 max-w-2xl mx-auto"></div>
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-8 max-w-3xl mx-auto"></div>
                        </div>
                    ) : (
                        <>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                                {t('aftercare.title.part1')} <span className="text-gold">{t('aftercare.title.part2')}</span>
                            </h2>
                            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                                {t('aftercare.subtitle')}
                            </p>
                        </>
                    )}
                </div>

                {!ready ? (
                    <div className="animate-pulse">
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="p-6 bg-gray-200 dark:bg-gray-700 rounded-xl">
                                    <div className="w-14 h-14 bg-gray-300 dark:bg-gray-600 rounded-xl mb-4"></div>
                                    <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-3"></div>
                                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                </div>
                            ))}
                        </div>
                        <div className="max-w-4xl mx-auto mt-16 p-8 bg-gray-200 dark:bg-gray-700 rounded-xl">
                            <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded mb-6 max-w-md mx-auto"></div>
                            <div className="grid md:grid-cols-3 gap-6">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="text-center">
                                        <div className="w-16 h-16 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-3"></div>
                                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                                        <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="mt-16 p-8 bg-gray-200 dark:bg-gray-700 rounded-xl">
                            <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded mb-4 max-w-sm mx-auto"></div>
                            <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-6 max-w-2xl mx-auto"></div>
                            <div className="h-12 bg-gray-300 dark:bg-gray-600 rounded-xl w-64 mx-auto"></div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div ref={cardsRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                            {getAftercareItems().map((item, index) => (
                        <Card key={index} className="h-full border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 hover:border-gold/50">
                            <CardContent className="p-6 h-full flex flex-col">
                                <div className="w-14 h-14 bg-gold/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-gold/30 transition-all duration-300">
                                    <item.icon className="w-7 h-7 text-gold" />
                                </div>

                                <h3 className="text-lg font-bold text-navy dark:text-white mb-3 leading-tight">
                                    {item.title}
                                </h3>

                                <p className={`text-slate-600 dark:text-slate-300 leading-relaxed flex-1 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                                    {item.description}
                                </p>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Recovery Timeline */}
                <div ref={timelineRef} className="max-w-4xl mx-auto mt-16">
                    <Card className="bg-gold/10 border-gold/30">
                        <CardContent className="p-8">
                            <h3 className="text-2xl font-bold text-navy dark:text-white mb-6 text-center">
                                {t('aftercare.timeline.title')}
                            </h3>

                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <span className="text-xl font-bold text-gold">{isRTL ? '١' : '1'}</span>
                                    </div>
                                    <h4 className="font-semibold text-navy dark:text-white mb-2">{t('aftercare.timeline.phases.first24.title')}</h4>
                                    <p className={`text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'text-right' : 'text-center'}`}>
                                        {t('aftercare.timeline.phases.first24.description')}
                                    </p>
                                </div>

                                <div className="text-center">
                                    <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <span className="text-xl font-bold text-gold">{isRTL ? '٢' : '2'}</span>
                                    </div>
                                    <h4 className="font-semibold text-navy dark:text-white mb-2">{t('aftercare.timeline.phases.week1.title')}</h4>
                                    <p className={`text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'text-right' : 'text-center'}`}>
                                        {t('aftercare.timeline.phases.week1.description')}
                                    </p>
                                </div>

                                <div className="text-center">
                                    <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <CheckCircle className="w-8 h-8 text-gold" />
                                    </div>
                                    <h4 className="font-semibold text-navy dark:text-white mb-2">{t('aftercare.timeline.phases.week2.title')}</h4>
                                    <p className={`text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'text-right' : 'text-center'}`}>
                                        {t('aftercare.timeline.phases.week2.description')}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Downloadable Recovery Guide */}
                <div ref={guideRef} className="mt-16">
                    <Card className="bg-gold/10 border-gold/30">
                        <CardContent className="p-8 text-center">
                            <div className={`flex items-center justify-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <Download className="w-8 h-8 text-gold" />
                                <h3 className="text-2xl font-bold text-navy dark:text-white">
                                    {t('aftercare.guide.title')}
                                </h3>
                            </div>
                            <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed text-center">
                                {t('aftercare.guide.description')}
                            </p>

                            <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                                <Button
                                    onClick={generateAftercarePDF}
                                    className={`bg-gold hover:bg-gold-600 text-navy px-6 py-3 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                                >
                                    <Download className="w-5 h-5" />
                                    {t('aftercare.guide.downloadButton')}
                                </Button>

                                <p className={`text-sm text-slate-600 dark:text-slate-300 flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <span>{t('aftercare.guide.faqPrompt')}</span>
                                    <a
                                        href="#faq"
                                        className="text-gold hover:text-gold-600 font-semibold underline"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const faqSection = document.getElementById('faq');
                                            faqSection?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                    >
                                        {t('aftercare.guide.faqLink')}
                                    </a>
                                </p>
                            </div>
                        </CardContent>
                        </Card>
                    </div>
                    </>
                )}
            </div>
        </section>
    );
} 